import React, { useState, useRef } from 'react';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import styled from 'styled-components';
import Colors from '@components/shared/Colors';

export enum CodeCopyStyle {
  Default,
  UseCode
}
interface Props {
  code: string;
  mobileOneRowBreakpoint?: number;
  style?: CodeCopyStyle;
  background?: string;
}

const CodeCopy = ({ background, code, mobileOneRowBreakpoint, style = CodeCopyStyle.Default }: Props) => {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const codeRef = useRef(null);

  const copyToClipboard = () => {
    if (!codeRef.current) return;
    const selection = window.getSelection();
    const range = document.createRange();

    range.selectNodeContents(codeRef.current);
    selection.removeAllRanges();
    selection.addRange(range);
    document.execCommand('copy');
    selection.removeAllRanges();

    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!code) return <div></div>;

  if (style === CodeCopyStyle.UseCode) {
    return (
      <UseCodeWrap onClick={copyToClipboard}>
        <UseCodeText>
          <span>Use code:</span>
          <CodeBox ref={codeRef}>{code}</CodeBox>
        </UseCodeText>
        {copied && (
          <CopiedNotification className="use-code">
            {t('components.code-copied-alt')}
            <Tag />
          </CopiedNotification>
        )}
      </UseCodeWrap>
    );
  }

  return (
    <CodeWrap $background={background}>
      <ReferralBox onClick={copyToClipboard} $mobileOneRowBreakpoint={mobileOneRowBreakpoint}>
        <CodeBox ref={codeRef}>{code}</CodeBox>
        <CopyButton>{t('components.copy-code')}</CopyButton>
      </ReferralBox>
      {copied && (
        <CopiedNotification>
          {t('components.code-copied-alt')}
          <Tag />
        </CopiedNotification>
      )}
    </CodeWrap>
  );
};

export default CodeCopy;

const CodeWrap = styled.div<{ $background?: string }>`
  position: relative;
  display: flex;
  justify-content: center;
  width: 100%;
  background: ${(props) => (props.$background ? props.$background : 'transparent')};
`;

const UseCodeWrap = styled(CodeWrap)`
  height: 50px;
  padding: 11px 16px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  border-radius: 5px;
  border: 1px solid #faead6;
  background: #fcf2de;
  cursor: pointer;

  @media (max-width: 768px) {
    padding: 13px 10px;
    height: auto;
  }
`;

const CopiedNotification = styled.div`
  position: absolute;
  top: -62px;
  left: 50%;
  transform: translateX(-50%);
  white-space: nowrap;
  padding: 15px;
  background: ${Colors.navyBlue};
  opacity: 1;
  color: ${Colors.cream};
  border-radius: 4px;
  font-family: 'BrownStd';
  font-size: 14px;
  font-style: normal;
  line-height: 20px;
  letter-spacing: 0;
  font-weight: 400;

  &.use-code {
    width: 200px;
    white-space: normal;
    top: -86px;
  }
`;

const Tag = styled.div`
  position: absolute;
  left: 50%;
  width: 15px;
  height: 15px;
  transform: rotate(45deg);
  background: ${Colors.navyBlue};
  bottom: -7px;
`;

const ReferralBox = styled.div<{ $mobileOneRowBreakpoint?: number }>`
  margin: 0;
  display: flex;
  justify-content: center;
  border: 1px solid ${Colors.paleGold};
  border-radius: 2px;
  width: 100%;

  @media only screen and (max-width: ${({ $mobileOneRowBreakpoint }) =>
      $mobileOneRowBreakpoint || $mobileOneRowBreakpoint === 0 ? $mobileOneRowBreakpoint : 768}px) {
    flex-direction: column;
  }
`;

const CodeBox = styled.div<{ $hidden?: boolean }>`
  font-family: 'BrownStd';
  text-align: center;
  font-family: BrownStd;
  font-size: 12px;
  font-style: normal;
  font-weight: 700;
  line-height: 120%; /* 14.4px */
  letter-spacing: 0.84px;
  text-transform: uppercase;
  padding: 13px 15px;
  white-space: nowrap;
  flex-grow: 1;
  display: ${(props) => (props.$hidden ? 'none' : 'block')};
`;

const CopyButton = styled.button`
  border: 0;
  font-family: 'BrownStd';
  text-align: center;
  font-family: BrownStd;
  font-size: 12px;
  font-style: normal;
  font-weight: 700;
  line-height: 16px; /* 133.333% */
  letter-spacing: 1px;
  text-transform: uppercase;
  color: ${Colors.navyBlue};
  background: ${Colors.paleGold};
  cursor: pointer;
  white-space: nowrap;
  height: 42px;
  padding: 0px 25px;
  flex-grow: 1;

  &:hover {
    color: ${Colors.paleGold};
    background-color: ${Colors.navyBlue};
  }
`;

const UseCodeText = styled.div`
  color: ${Colors.lilac};
  text-align: center;
  font-family: 'BrownStd';
  font-size: 16px;
  font-weight: 700;
  line-height: 140%;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;

  @media (max-width: 320px) {
    flex-direction: column;
  }

  & > span {
    color: ${Colors.navyBlue};
    font-weight: 400;
  }

  & > div {
    color: ${Colors.lilac};
    font-weight: bold;
    font-size: 16px;
    letter-spacing: 0.8px;
    padding: 0;
  }
`;
